import type * as THREE from 'three';
import { settings } from '../settings.ts';
import type { WorstGap } from './live.ts';

/** A screen shorter than this sends nothing (a glance at the bastion says little). */
const MIN_SECS = 20;
/** One frame in this many is timed on the GPU (a timer query per frame costs on some drivers). */
const GPU_EVERY = 20;
const KEEP = 1800;
/** A frame this long (from one drawing to the next) is a hitch the player sees. */
const HITCH_MS = 100;
/** The worst hitches kept, each with what the page was doing. */
const WORST = 12;
/** A tick from the room this late after the one before reads as the network stalling. */
const LATE_MS = 250;

/**
 * Time spent in the calls that wait for a shader program to finish linking (three asks them on a program's first use).
 * The context is wrapped once; every meter reads the total going up.
 */
const linkWait = new WeakMap<object, { ms: number }>();
const watchLinks = (gl: WebGL2RenderingContext): { ms: number } => {
  const known = linkWait.get(gl);
  if (known) return known;
  const acc = { ms: 0 };
  linkWait.set(gl, acc);
  const g = gl as unknown as Record<string, unknown>;
  for (const name of ['getProgramParameter', 'getProgramInfoLog', 'getShaderInfoLog', 'getShaderParameter']) {
    const f = g[name] as ((...a: unknown[]) => unknown) | undefined;
    if (typeof f !== 'function') continue;
    g[name] = (...a: unknown[]): unknown => {
      const t = performance.now();
      try {
        return f.apply(gl, a);
      } finally {
        acc.ms += performance.now() - t;
      }
    };
  }
  return acc;
};

/** One frame that took too long: when, how long, and what the page was doing then. */
export interface Hitch {
  /** Seconds into the screen. */
  s: number;
  /** From the drawing before to this one, ms. */
  ms: number;
  /** The loop's own work in that frame (JS and GL calls), ms. */
  cpu: number;
  /** Shader programs, geometries and textures that appeared in that frame (a compile, an upload). */
  prog: number;
  geo: number;
  tex: number;
  /** The longest browser long task that ended in that frame, ms (GC, parsing, anything off the loop). */
  task: number;
  /** Of those, waiting on the GPU process for a shader program (its link status and logs, three's first use), ms. */
  link: number;
  /** Live: the room's messages handled since the drawing before (its ticks simulated and drawn into the view), ms. */
  net?: number;
  /** What the screen says was going on (room, enemies, a kind seen for the first time), and `p:` the programs compiled in it. */
  ctx: string;
}

/** What the live link saw (LiveLink.seen). */
export interface NetSeen {
  gaps: number[];
  rtts: number[];
  reconnects: number;
  /** The socket's negotiated extensions ("permessage-deflate…" when compressed), and the biggest message, in characters. */
  ext: string;
  big: number;
  worst: WorstGap[];
}

/** The p-th quantile (0..1) of a list, rounded to tenths; null when empty. */
export const quantile = (xs: readonly number[], p: number): number | null => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  return Math.round((s[Math.min(s.length - 1, Math.floor(s.length * p))] as number) * 10) / 10;
};

export interface PerfSummary {
  mode: string;
  live: boolean;
  secs: number;
  /** Frames drawn each second: 10th percentile, median, 90th. */
  fps: [number | null, number | null, number | null];
  /** The loop's own work per frame in ms (JS and GL calls, not the GPU): median, 90th. */
  cpu: [number | null, number | null];
  /** GPU time per frame in ms, where the browser can time it: median, 90th. */
  gpu: [number | null, number | null] | null;
  /** Share of seconds with the 30 fps cap on. */
  capped: number;
  w: number;
  h: number;
  pr: number;
  quality: string;
  tilt: boolean;
  gl: string;
  agent: string;
  build: string;
  /** Frames over 50, 100 and 250 ms (from one drawing to the next), and the longest one. */
  long?: [number, number, number];
  worst?: number;
  /** The worst hitches, in the order they happened. */
  hitches?: Hitch[];
  /** Browser long tasks (Chromium only): how many, their total and the longest, ms. */
  tasks?: [number, number, number] | null;
  /** JS heap at its highest in MB (Chromium only), and the renderer's geometries, textures and programs at the end. */
  mem?: { heap: number | null; geo: number; tex: number; prog: number };
  /** The driver compiles shaders off the main thread (KHR_parallel_shader_compile). */
  par?: boolean;
  /** Cores and memory in GB the browser reports. */
  dev?: { cores: number; ram: number | null };
  /** Live: round trip (median, 95th, max), wait between ticks (95th, max), ticks over LATE_MS late, reconnects, resyncs, the socket's extensions, its biggest message and the three longest waits between ticks. */
  net?: { rtt: [number | null, number | null, number | null]; gap: [number | null, number | null]; late: number; reconnects: number; resyncs: number; ext?: string; big?: number; worst?: WorstGap[] } | null;
}

/**
 * How a screen runs on the player's device, sent once when it closes: frames per second, the loop's cost per
 * frame, the GPU's where it can be timed, and which GPU and screen it was. A phone stuck at 15 fps is otherwise
 * a mystery: GPU-bound, CPU-bound, or the browser drawing without the GPU at all.
 */
export class PerfMeter {
  private fps: number[] = [];
  private cpu: number[] = [];
  private gpu: number[] = [];
  private capped = 0;
  private frames = 0;
  private second = 0;
  private n = 0;
  private ext: { TIME_ELAPSED_EXT: number; GPU_DISJOINT_EXT: number } | null;
  private active: WebGLQuery | null = null;
  private pending: WebGLQuery[] = [];
  private gl: WebGL2RenderingContext;
  private start = 0;
  private lastEnd = 0;
  private long: [number, number, number] = [0, 0, 0];
  private worst = 0;
  private hitches: Hitch[] = [];
  private was = { prog: 0, geo: 0, tex: 0, link: 0 };
  private links: { ms: number };
  /** Programs already seen, to name the ones a hitch compiled. */
  private progs = new Set<unknown>();
  private fresh = '';
  private tasks: [number, number, number] = [0, 0, 0];
  private taskNow = 0;
  private observer: PerformanceObserver | null = null;
  private heap = 0;
  private heapAt = 0;
  /** The tab went hidden since the last drawing: the gap that follows is the player away, not a hitch. */
  private away = false;
  private onHide = (): void => {
    if (document.visibilityState !== 'visible') this.away = true;
  };
  /** The longest frame since someone last asked (the live link sends it with each ping). */
  jank = 0;
  /** What the screen was doing, asked only when a hitch happens. */
  context: () => string = () => '';
  /** Live: what the link saw, and the client's resyncs. */
  net: (() => { seen: NetSeen; resyncs: number }) | null = null;
  /** Live: the time spent on the room's messages since last asked (LiveLink.busy, taken). */
  netBusy: (() => number) | null = null;

  constructor(
    private mode: string,
    private live: boolean,
    private renderer: THREE.WebGLRenderer,
  ) {
    this.gl = renderer.getContext() as WebGL2RenderingContext;
    this.ext = this.gl.getExtension('EXT_disjoint_timer_query_webgl2') as PerfMeter['ext'];
    this.links = watchLinks(this.gl);
    document.addEventListener('visibilitychange', this.onHide);
    try {
      if (PerformanceObserver.supportedEntryTypes?.includes('longtask')) {
        this.observer = new PerformanceObserver((list) => {
          for (const e of list.getEntries()) {
            this.tasks[0]++;
            this.tasks[1] += e.duration;
            this.tasks[2] = Math.max(this.tasks[2], e.duration);
            this.taskNow = Math.max(this.taskNow, e.duration);
          }
        });
        this.observer.observe({ type: 'longtask' });
      }
    } catch {
      this.observer = null;
    }
  }

  /** Around the frame's drawing. */
  begin(): void {
    if (!this.ext || this.active || this.n++ % GPU_EVERY) return;
    this.active = this.gl.createQuery();
    if (this.active) this.gl.beginQuery(this.ext.TIME_ELAPSED_EXT, this.active);
  }

  end(now: number, workMs: number, capped: boolean): void {
    const gl = this.gl;
    if (this.ext && this.active) {
      gl.endQuery(this.ext.TIME_ELAPSED_EXT);
      this.pending.push(this.active);
      this.active = null;
    }
    const q = this.pending[0];
    if (q && this.ext && gl.getQueryParameter(q, gl.QUERY_RESULT_AVAILABLE)) {
      this.pending.shift();
      if (!gl.getParameter(this.ext.GPU_DISJOINT_EXT) && this.gpu.length < KEEP) this.gpu.push((gl.getQueryParameter(q, gl.QUERY_RESULT) as number) / 1e6);
      gl.deleteQuery(q);
    }
    this.frames++;
    if (this.cpu.length < KEEP) this.cpu.push(workMs);
    this.hitch(now, workMs);
    if (!this.second) this.second = now;
    else if (now - this.second >= 1000) {
      // A hidden tab or a stall over a second isn't a frame rate.
      if (now - this.second < 2000 && this.fps.length < KEEP) {
        this.fps.push((this.frames * 1000) / (now - this.second));
        if (capped) this.capped++;
      }
      this.second = now;
      this.frames = 0;
    }
  }

  /** From one drawing to the next: counted, and the worst kept with what the page was doing. */
  private hitch(now: number, workMs: number): void {
    const info = this.renderer.info;
    const is = { prog: info.programs?.length ?? 0, geo: info.memory.geometries, tex: info.memory.textures, link: this.links.ms };
    if (is.prog !== this.was.prog) this.fresh = this.newPrograms();
    const gap = this.lastEnd ? now - this.lastEnd : 0;
    const busy = this.netBusy?.() ?? null;
    this.start ||= now;
    this.lastEnd = now;
    // A hidden tab stops drawing: that gap is no hitch. The check has to come from the moment it hid, since the
    // first drawing after coming back already sees the tab visible.
    const away = this.away || document.visibilityState !== 'visible';
    this.away = false;
    if (gap > 0 && gap < 5000 && !away) {
      this.jank = Math.max(this.jank, gap);
      if (gap > 50) this.long[0]++;
      if (gap > 100) this.long[1]++;
      if (gap > 250) this.long[2]++;
      this.worst = Math.max(this.worst, gap);
      const least = this.hitches.length < WORST ? 0 : Math.min(...this.hitches.map((x) => x.ms));
      if (gap > HITCH_MS && gap > least) {
        const r = (x: number): number => Math.round(x);
        const h: Hitch = { s: r((now - this.start) / 1000), ms: r(gap), cpu: r(workMs), prog: is.prog - this.was.prog, geo: is.geo - this.was.geo, tex: is.tex - this.was.tex, task: r(this.taskNow), link: r(is.link - this.was.link), ...(busy !== null ? { net: r(busy) } : {}), ctx: `${this.context()}${this.fresh ? ` p:${this.fresh}` : ''}`.slice(0, 80) };
        if (this.hitches.length >= WORST) this.hitches.splice(this.hitches.findIndex((x) => x.ms === least), 1);
        this.hitches.push(h);
      }
    }
    this.was = is;
    this.fresh = '';
    this.taskNow = 0;
    // Once a second: reading it every frame was 1% of a phone's busy time.
    if (now - this.heapAt >= 1000) {
      this.heapAt = now;
      const mem = (performance as { memory?: { usedJSHeapSize: number } }).memory;
      if (mem) this.heap = Math.max(this.heap, mem.usedJSHeapSize);
    }
  }

  /** The programs that appeared since last asked, by their material's name (`?` without one). */
  private newPrograms(): string {
    const out: string[] = [];
    for (const p of this.renderer.info.programs ?? []) {
      if (this.progs.has(p)) continue;
      this.progs.add(p);
      if (this.start) out.push(((p as { name?: string }).name || '?').split('|')[0]?.replace('shared:', '').replace('Material', '') ?? '?');
    }
    return out.join(',');
  }

  summary(): PerfSummary {
    const c = this.renderer.domElement;
    const info = this.gl.getExtension('WEBGL_debug_renderer_info');
    const gl = info ? String(this.gl.getParameter(info.UNMASKED_RENDERER_WEBGL)) : String(this.gl.getParameter(this.gl.RENDERER));
    return {
      mode: this.mode,
      live: this.live,
      secs: this.fps.length,
      fps: [quantile(this.fps, 0.1), quantile(this.fps, 0.5), quantile(this.fps, 0.9)],
      cpu: [quantile(this.cpu, 0.5), quantile(this.cpu, 0.9)],
      gpu: this.gpu.length ? [quantile(this.gpu, 0.5), quantile(this.gpu, 0.9)] : null,
      capped: this.fps.length ? Math.round((this.capped / this.fps.length) * 100) / 100 : 0,
      w: c.width,
      h: c.height,
      pr: Math.round(this.renderer.getPixelRatio() * 100) / 100,
      quality: settings.quality,
      tilt: settings.tiltShift,
      gl: gl.slice(0, 120),
      agent: navigator.userAgent.slice(0, 200),
      build: __BUILD__,
      long: this.long,
      worst: Math.round(this.worst),
      hitches: this.hitches,
      tasks: this.observer ? [this.tasks[0], Math.round(this.tasks[1]), Math.round(this.tasks[2])] : null,
      mem: { heap: this.heap ? Math.round(this.heap / 1e6) : null, geo: this.renderer.info.memory.geometries, tex: this.renderer.info.memory.textures, prog: this.renderer.info.programs?.length ?? 0 },
      par: !!this.gl.getExtension('KHR_parallel_shader_compile'),
      dev: { cores: navigator.hardwareConcurrency || 0, ram: (navigator as { deviceMemory?: number }).deviceMemory ?? null },
      net: this.netSummary(),
    };
  }

  private netSummary(): PerfSummary['net'] {
    const n = this.net?.();
    if (!n) return null;
    const { gaps, rtts, reconnects, ext, big, worst } = n.seen;
    return {
      rtt: [quantile(rtts, 0.5), quantile(rtts, 0.95), rtts.length ? Math.max(...rtts) : null],
      gap: [quantile(gaps, 0.95), gaps.length ? Math.round(Math.max(...gaps)) : null],
      late: gaps.filter((g) => g > LATE_MS).length,
      reconnects,
      resyncs: n.resyncs,
      ext: ext.slice(0, 200),
      big,
      worst,
    };
  }

  /** The screen closes: what it measured goes to the server (nothing in development or for a short visit). */
  send(): void {
    for (const q of this.pending) this.gl.deleteQuery(q);
    this.pending = [];
    this.observer?.disconnect();
    document.removeEventListener('visibilitychange', this.onHide);
    if (import.meta.env.DEV || this.fps.length < MIN_SECS) return;
    try {
      void fetch('/api/client-perf', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(this.summary()), keepalive: true }).catch(() => undefined);
    } catch {
      /* nothing to do */
    }
  }
}
